import {useAui, useAuiEvent} from '@assistant-ui/react';
import {createContext, useCallback, useContext, useState} from 'react';

type SuppressAutoscroll = () => void;

const NOOP_SUPPRESS: SuppressAutoscroll = () => {};

export const WBAgentAutoscrollContext =
  createContext<SuppressAutoscroll>(NOOP_SUPPRESS);

/**
 * Returns a "suppress autoscroll" function, which can disable AUI's resize-
 * triggered autoscroll.
 *
 * Must be rendered inside a `WBAgentAutoscrollContext.Provider` for
 * suppression to actually take effect.
 */
export const useSuppressWBAgentAutoscroll = (): SuppressAutoscroll =>
  useContext(WBAgentAutoscrollContext);

/**
 * Owns the "autoscroll suppressed" flag consumed by
 * `<ThreadPrimitive.Viewport autoScroll={!suppressed} ...>`
 *
 * Suppression only engages when the thread is idle, it holds until
 * the next `thread.runStart` (i.e. the user sends their next message).
 *
 * Must be called from a component rendered inside `AuiProvider`.
 */
export function useWBAgentAutoscrollSuppression(): {
  suppressed: boolean;
  suppress: SuppressAutoscroll;
} {
  const aui = useAui();
  const [suppressed, setSuppressed] = useState(false);

  const suppress = useCallback<SuppressAutoscroll>(() => {
    if (aui.thread().getState().isRunning) {
      return;
    }
    setSuppressed(true);
  }, [aui]);

  useAuiEvent('thread.runStart', () => {
    setSuppressed(false);
  });

  return {suppressed, suppress};
}
